import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Trophy, TrendingUp, Zap, RefreshCw, LayoutList } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import BackButton from '../components/BackButton';
import EnhancedTradingPairCard from '../components/trading-new/EnhancedTradingPairCard';
import BinaryTradingView from '../components/trading-new/BinaryTradingView';
import AIArbitrageView from '../components/trading-new/AIArbitrageView';
import CryptoConversionView from '../components/trading-new/CryptoConversionView';
import EnhancedActiveOrdersView from '../components/trading-new/EnhancedActiveOrdersView';
import EnhancedHistoryView from '../components/trading-new/EnhancedHistoryView';
import { useCryptoData } from '../hooks/useCryptoData';
import { getQuoteCurrency, getCommodity } from '../utils/commodities';
import PriceAlertButton from '../components/PriceAlertButton';
import PriceAlertsPanel from '../components/PriceAlertsPanel';
import { useUnifiedBalance } from '../hooks/useUnifiedBalance';
import { tradingAPI } from '../services/api';
import BalanceDisplay from '../components/ui/BalanceDisplay';

import { fmtFixed } from '../utils/format';
const TradingNew = () => {
  const navigate = useNavigate();
  const { cryptos, loading } = useCryptoData(20);
  const { totalBalance, availableBalance, loading: balanceLoading, refresh: refreshBalance } = useUnifiedBalance();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Handle balance refresh
  const handleRefreshBalance = async () => {
    setIsRefreshing(true);
    try {
      await refreshBalance();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };
  const [selectedMode, setSelectedMode] = useState('binary');
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'trade');

  // Deep link (?tab=history) + "View in History" from the trade report
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'trade' || tab === 'active' || tab === 'history') setActiveTab(tab);
  }, [location.search]);
  useEffect(() => {
    const open = () => setActiveTab('history');
    window.addEventListener('phantomtrades:open-history', open);
    return () => window.removeEventListener('phantomtrades:open-history', open);
  }, []);
  const [selectedPair, setSelectedPair] = useState(null);

  // Get top 20 cryptocurrencies for pair selection
  const topCryptos = cryptos.slice(0, 20);

  const tradingPanelRef = useRef(null);

  // Jump straight to the chart + trade form once a pair is picked
  const handlePairSelect = (crypto) => {
    setSelectedPair(crypto);
    requestAnimationFrame(() => {
      const el = tradingPanelRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  };

  const backToPairs = () => {
    setSelectedPair(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navbar isDashboard={true} />
      <MobileHeader isDashboard={true} />

      <div className="pt-20 sm:pt-24 pb-20 sm:pb-8 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-7xl">
          <BackButton label="Back" />

          {/* Hub Section - Mobile Responsive */}
          <div className="mb-4">
            {/* Desktop Layout */}
            <div className="hidden sm:flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-2 hover:bg-purple-500/10 rounded-lg transition-colors mt-1"
                >
                  <ArrowLeft className="h-5 w-5" style={{ color: 'var(--text-primary)' }} />
                </button>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: '#a855f7' }}>Hub</h2>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>All trading operations in one place</p>
                </div>
              </div>
              <div className="glow-card px-4 py-3 rounded-xl">
                <div className="text-right">
                  <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Account Balance</p>
                  {balanceLoading ? (
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
                      <div className="animate-pulse">Loading...</div>
                    </div>
                  ) : (
                    <BalanceDisplay 
                      balance={availableBalance} 
                      size="large" 
                      className="justify-end text-2xl sm:text-3xl" 
                      storageKey="trading_balance_visibility" 
                      currency="USDT"
                      showToggle={true}
                      showRefresh={true}
                      onRefresh={handleRefreshBalance}
                      isRefreshing={isRefreshing}
                    />
                  )}
                  <p className="text-xs flex items-center justify-end space-x-1 mt-1" style={{ color: 'var(--text-secondary)' }}>
                    <span className={`w-2 h-2 rounded-full ${isRefreshing ? 'bg-yellow-400 animate-pulse' : 'bg-green-500'}`}></span>
                    <span>{isRefreshing ? 'Syncing...' : 'Real-time sync'}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Layout - Stacked */}
            <div className="sm:hidden">
              {/* Header Row */}
              <div className="flex items-center space-x-2 mb-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-2 hover:bg-purple-500/10 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" style={{ color: 'var(--text-primary)' }} />
                </button>
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#a855f7' }}>Hub</h2>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>All trading operations</p>
                </div>
              </div>
              
              {/* Balance Card - Full Width on Mobile */}
              <div className="glow-card px-4 py-3 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Account Balance</p>
                    <div className="flex items-center space-x-1">
                      <span className={`w-2 h-2 rounded-full ${isRefreshing ? 'bg-yellow-400 animate-pulse' : 'bg-green-500'}`}></span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{isRefreshing ? 'Syncing...' : 'Synced'}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {balanceLoading ? (
                      <div className="text-xl font-bold text-white">
                        <div className="animate-pulse">Loading...</div>
                      </div>
                    ) : (
                      <BalanceDisplay 
                        balance={availableBalance} 
                        size="medium" 
                        className="justify-end" 
                        storageKey="trading_balance_visibility" 
                        currency="USDT"
                        showToggle={true}
                        showRefresh={true}
                        onRefresh={handleRefreshBalance}
                        isRefreshing={isRefreshing}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trading Modes - Single card horizontal with no gaps */}
          <div className="mb-4 glow-card rounded-xl overflow-hidden">
            <div className="flex divide-x divide-gray-700">
              <button
                data-testid="trading-mode-binary-btn"
                onClick={() => setSelectedMode('binary')}
                className={`flex-1 py-4 px-3 font-bold transition-all text-sm sm:text-base flex items-center justify-center space-x-2 relative overflow-hidden ${
                  selectedMode === 'binary'
                    ? 'bg-gradient-to-b from-green-500 to-green-700 text-white'
                    : 'text-white/60 hover:text-white hover:bg-gradient-to-b hover:from-orange-400/30 hover:to-orange-600/30'
                }`}
              >
                {selectedMode === 'binary' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                )}
                <TrendingUp className="h-5 w-5 relative z-10" />
                <span className="relative z-10">Binary Trading</span>
              </button>
              <button
                data-testid="trading-mode-arbitrage-btn"
                onClick={() => setSelectedMode('arbitrage')}
                className={`flex-1 py-4 px-3 font-bold transition-all text-sm sm:text-base flex items-center justify-center space-x-2 relative overflow-hidden ${
                  selectedMode === 'arbitrage'
                    ? 'bg-gradient-to-b from-yellow-500 to-orange-600 text-white'
                    : 'text-white/60 hover:text-white hover:bg-gradient-to-b hover:from-orange-400/30 hover:to-orange-600/30'
                }`}
              >
                {selectedMode === 'arbitrage' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                )}
                <Zap className="h-5 w-5 relative z-10" />
                <span className="relative z-10">AI Arbitrage</span>
              </button>
              <button
                onClick={() => setSelectedMode('conversion')}
                className={`flex-1 py-4 px-3 font-bold transition-all text-sm sm:text-base flex items-center justify-center space-x-2 relative overflow-hidden ${
                  selectedMode === 'conversion'
                    ? 'bg-gradient-to-b from-blue-500 to-blue-700 text-white'
                    : 'text-white/60 hover:text-white hover:bg-gradient-to-b hover:from-orange-400/30 hover:to-orange-600/30'
                }`}
              >
                {selectedMode === 'conversion' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                )}
                <RefreshCw className="h-5 w-5 relative z-10" />
                <span className="relative z-10">Crypto Conversion</span>
              </button>
            </div>
          </div>

          {/* Activity Tabs - Single card horizontal with no gaps */}
          <div className="mb-4 glow-card rounded-xl overflow-hidden">
            <div className="flex divide-x divide-gray-700">
              <button
                onClick={() => setActiveTab('trade')}
                className={`flex-1 py-3 px-4 font-semibold transition-all text-sm sm:text-base relative overflow-hidden ${
                  activeTab === 'trade'
                    ? 'activity-tab-selected'
                    : 'activity-tab-unselected'
                }`}
              >
                {activeTab === 'trade' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                )}
                <span className="relative z-10">Trade</span>
              </button>
              <button
                onClick={() => setActiveTab('active')}
                className={`flex-1 py-3 px-4 font-semibold transition-all text-sm sm:text-base relative overflow-hidden ${
                  activeTab === 'active'
                    ? 'activity-tab-selected'
                    : 'activity-tab-unselected'
                }`}
              >
                {activeTab === 'active' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                )}
                <span className="relative z-10">Active</span>
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 py-3 px-4 font-semibold transition-all text-sm sm:text-base relative overflow-hidden ${
                  activeTab === 'history'
                    ? 'activity-tab-selected'
                    : 'activity-tab-unselected'
                }`}
              >
                {activeTab === 'history' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                )}
                <span className="relative z-10">History</span>
              </button>
            </div>
          </div>

          {/* Trade Tab Content */}
          {activeTab === 'trade' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left Side: Crypto Pair List - hidden on mobile while a pair is open */}
              <div className={`lg:col-span-1 ${selectedPair ? 'hidden lg:block' : ''}`} data-testid="pairs-column">
                <div className="glow-card rounded-xl overflow-hidden">
                  {/* Enhanced Sticky Header with Solid Background */}
                  <div className="sticky top-0 z-10 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border-b-2 border-purple-500/40 shadow-lg">
                    <div className="px-3 sm:px-4 py-3 sm:py-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="p-1.5 sm:p-2 bg-purple-600/20 rounded-lg">
                            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-bold text-base sm:text-lg md:text-xl leading-tight">
                              Trading Pairs
                            </h3>
                            <p className="text-gray-400 text-xs sm:text-sm hidden sm:block">
                              Select crypto to trade
                            </p>
                          </div>
                        </div>
                        {/* Live indicator */}
                        <div className="flex items-center space-x-1.5 px-2 sm:px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-full">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-green-400 text-xs font-semibold hidden sm:inline">Live</span>
                        </div>
                      </div>
                      {/* Mobile subtitle */}
                      <p className="text-gray-400 text-xs sm:hidden">
                        Tap to select • Live prices
                      </p>
                    </div>
                  </div>
                  
                  {/* Scrollable List */}
                  <div className="p-3 sm:p-4 max-h-[500px] sm:max-h-[600px] overflow-y-auto">
                    {loading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
                      </div>
                    ) : (
                      <div className="space-y-1.5 sm:space-y-2">
                      {topCryptos.map((crypto, index) => (
                        <button
                          key={crypto.id}
                          data-testid={`pair-select-${crypto.symbol.toLowerCase()}`}
                          onClick={() => handlePairSelect(crypto)}
                          className={`w-full p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all text-left group relative overflow-hidden ${
                            selectedPair?.id === crypto.id
                              ? 'bg-gradient-to-r from-purple-600/30 to-purple-800/30 border-2 border-purple-500 shadow-lg shadow-purple-500/20 sm:scale-105'
                              : 'bg-gray-800/50 border-2 border-transparent hover:bg-gray-800 hover:border-purple-500/30 sm:hover:scale-102 active:scale-98'
                          }`}
                        >
                          {/* Purple highlight for selected */}
                          {selectedPair?.id === crypto.id && (
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent animate-pulse"></div>
                          )}
                          
                          <div className="relative flex items-center space-x-2 sm:space-x-3">
                            {/* Rank Badge */}
                            <div className="flex-shrink-0">
                              <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                index < 3 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-400'
                              }`}>
                                {index + 1}
                              </div>
                            </div>
                            
                            {/* Crypto Icon */}
                            <div className="relative flex-shrink-0">
                              {selectedPair?.id === crypto.id && (
                                <div className="absolute inset-0 bg-purple-500 blur-md opacity-50 hidden sm:block"></div>
                              )}
                              <img 
                                src={crypto.image} 
                                alt={crypto.name}
                                className={`relative w-8 h-8 sm:w-10 sm:h-10 rounded-full ${
                                  selectedPair?.id === crypto.id ? 'ring-2 ring-purple-400' : ''
                                }`}
                              />
                            </div>
                            
                            {/* Crypto Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-1 sm:space-x-2 mb-0.5">
                                <span className={`font-bold text-sm sm:text-base ${
                                  selectedPair?.id === crypto.id ? 'text-purple-300' : 'text-white'
                                }`}>
                                  {crypto.symbol.toUpperCase()}
                                </span>
                                <span className="text-xs text-gray-500">/{getQuoteCurrency(crypto.symbol)}</span>
                              </div>
                              <div className="text-xs text-gray-400 truncate hidden sm:block">{crypto.name}</div>
                            </div>
                            
                            {/* Price Info */}
                            <div className="text-right flex-shrink-0">
                              <div className={`font-semibold text-xs sm:text-sm mb-1 ${
                                selectedPair?.id === crypto.id ? 'text-white' : 'text-gray-200'
                              }`}>
                                ${crypto.current_price >= 1 
                                  ? crypto.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                  : fmtFixed(crypto.current_price, crypto.current_price >= 0.01 ? 4 : 6)
                                }
                              </div>
                              <div className={`text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded ${
                                crypto.price_change_percentage_24h >= 0 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {crypto.price_change_percentage_24h >= 0 ? '+' : ''}
                                {crypto.price_change_percentage_24h?.toFixed(2)}%
                              </div>
                            </div>

                            {/* Selected Indicator */}
                            {selectedPair?.id === crypto.id && (
                              <div className="absolute -right-1.5 sm:-right-2 top-1/2 -translate-y-1/2">
                                <div className="w-1 h-6 sm:h-8 bg-purple-500 rounded-l-full"></div>
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                      </div>
                    )}
                  </div>
                </div>
                <PriceAlertsPanel />
              </div>

              {/* Right Side: Trading View */}
              <div className="lg:col-span-2 scroll-mt-24" ref={tradingPanelRef} data-testid="trading-panel">
                {selectedPair ? (
                  <div>
                    <button
                      type="button"
                      onClick={backToPairs}
                      data-testid="back-to-pairs-btn"
                      className="lg:hidden mb-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-purple-300 bg-purple-500/10 border border-purple-500/30"
                    >
                      <LayoutList className="h-4 w-4" /> All pairs
                    </button>
                    {/* Selected Pair Header */}
                    <div className="glow-card p-4 rounded-xl mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={selectedPair.image} 
                            alt={selectedPair.name}
                            className="w-12 h-12 rounded-full"
                          />
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-2xl font-bold text-purple-400">
                                {selectedPair.symbol.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-400">/{getQuoteCurrency(selectedPair.symbol)}</span>
                            </div>
                            <div className="text-sm text-gray-400">{selectedPair.name}</div>
                            {getCommodity(selectedPair.symbol) && <div className="mt-2"><PriceAlertButton crypto={selectedPair} /></div>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-white">
                            ${selectedPair.current_price >= 1 
                              ? selectedPair.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : fmtFixed(selectedPair.current_price, 6)
                            }
                          </div>
                          <div className={`text-sm font-semibold inline-block px-2 py-1 rounded ${
                            selectedPair.price_change_percentage_24h >= 0 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {selectedPair.price_change_percentage_24h >= 0 ? '+' : ''}
                            {selectedPair.price_change_percentage_24h?.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Trading View */}
                    {selectedMode === 'binary' && (
                      <BinaryTradingView crypto={selectedPair} />
                    )}
                    {selectedMode === 'arbitrage' && (
                      <AIArbitrageView crypto={selectedPair} />
                    )}
                    {selectedMode === 'conversion' && (
                      <CryptoConversionView crypto={selectedPair} />
                    )}
                  </div>
                ) : (
                  <div className="glow-card rounded-xl p-12 text-center">
                    <div className="text-white/50 mb-2">
                      <TrendingUp className="h-16 w-16 mx-auto mb-4" />
                    </div>
                    <h3 className="text-white font-bold text-xl mb-2">Select a Trading Pair</h3>
                    <p className="text-gray-400">Choose a cryptocurrency from the list to start trading</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Active Orders Tab */}
          {activeTab === 'active' && selectedMode !== 'arbitrage' && <EnhancedActiveOrdersView />}
          {activeTab === 'active' && selectedMode === 'arbitrage' && <AIArbitrageView crypto={selectedPair} initialTab="active" />}

          {/* History Tab */}
          {activeTab === 'history' && selectedMode === 'binary' && <EnhancedHistoryView />}
          {activeTab === 'history' && selectedMode === 'arbitrage' && <AIArbitrageView crypto={selectedPair} initialTab="history" />}
          {activeTab === 'history' && selectedMode === 'conversion' && <CryptoConversionView initialTab="history" />}
        </div>
      </div>
      
    </div>
  );
};

export default TradingNew;
